type BootstrapCollapseInstance = {
    toggle(): void;
};

type BootstrapCollapseConstructor = {
    getOrCreateInstance(element: Element, config?: { toggle?: boolean }): BootstrapCollapseInstance;
};

type WindowWithBootstrap = Window & {
    bootstrap?: {
        Collapse?: BootstrapCollapseConstructor;
    };
};

const installedDocuments = new WeakSet<Document>();
const legacyCollapseTriggerSelector = [
    '[data-toggle="collapse"][data-target]',
    '[data-toggle="collapse"][href]',
    '[data-bs-toggle="collapse"][data-bs-target]',
    '[data-bs-toggle="collapse"][href]',
    '.card-header[data-target]',
    '.card-header[data-bs-target]'
].join(', ');

function resolveCollapseTarget(trigger: Element, documentRef: Document): HTMLElement | null {
    const selector = trigger.getAttribute('data-bs-target') || trigger.getAttribute('data-target') || trigger.getAttribute('href');

    if (!selector || selector === '#') {
        return null;
    }

    try {
        const target = documentRef.querySelector(selector);
        return target instanceof HTMLElement ? target : null;
    } catch {
        return null;
    }
}

function getLegacyCollapseTriggers(target: HTMLElement, documentRef: Document): HTMLElement[] {
    return Array.from(documentRef.querySelectorAll<HTMLElement>(legacyCollapseTriggerSelector)).filter(
        (trigger) => resolveCollapseTarget(trigger, documentRef) === target
    );
}

function syncLegacyCollapseTriggers(target: HTMLElement, expanded: boolean, documentRef: Document): void {
    getLegacyCollapseTriggers(target, documentRef).forEach((trigger) => {
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        trigger.classList.toggle('collapsed', !expanded);
    });
}

function isAppStudioCardHeader(trigger: HTMLElement): boolean {
    return trigger.classList.contains('card-header');
}

function isCardHeaderHelpInteraction(eventTarget: EventTarget | null, trigger: HTMLElement): boolean {
    return (
        isAppStudioCardHeader(trigger) && eventTarget instanceof Element && eventTarget.closest('.detailsQuote, .card-header-help') !== null
    );
}

function findCollapseTrigger(eventTarget: EventTarget | null): HTMLElement | null {
    return eventTarget instanceof Element ? eventTarget.closest<HTMLElement>(legacyCollapseTriggerSelector) : null;
}

function fallbackToggle(target: HTMLElement, documentRef: Document): void {
    const expanded = !target.classList.contains('show');

    target.classList.remove('collapsing');
    target.classList.add('collapse');
    target.classList.toggle('show', expanded);
    target.style.height = '';

    syncLegacyCollapseTriggers(target, expanded, documentRef);
}

function activateCollapseTrigger(
    event: Event,
    trigger: HTMLElement,
    eventTarget: EventTarget | null,
    windowRef: WindowWithBootstrap,
    documentRef: Document
): void {
    const target = resolveCollapseTarget(trigger, documentRef);
    if (!target) {
        return;
    }

    if (isCardHeaderHelpInteraction(eventTarget, trigger)) {
        event.stopImmediatePropagation();
        return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    // Bootstrap's delegated data API is registered before this compatibility
    // handler and runs in the capture phase too. If it has already started the
    // transition, leave it to finish instead of interpreting the temporary
    // absence of `show` as a request to reopen the section.
    if (target.classList.contains('collapsing')) {
        return;
    }

    const Collapse = windowRef.bootstrap?.Collapse;
    if (Collapse && !isAppStudioCardHeader(trigger)) {
        Collapse.getOrCreateInstance(target, { toggle: false }).toggle();
    } else {
        fallbackToggle(target, documentRef);
    }
}

export function installLegacyCollapseCompatibility(
    windowRef: WindowWithBootstrap = window,
    documentRef: Document = windowRef.document
): void {
    if (installedDocuments.has(documentRef)) {
        return;
    }

    installedDocuments.add(documentRef);

    documentRef.addEventListener(
        'click',
        (event) => {
            const eventTarget = event.target;
            const trigger = findCollapseTrigger(eventTarget);

            if (!trigger) {
                return;
            }

            activateCollapseTrigger(event, trigger, eventTarget, windowRef, documentRef);
        },
        true
    );

    documentRef.addEventListener(
        'keydown',
        (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }

            const eventTarget = event.target;
            const trigger = findCollapseTrigger(eventTarget);

            if (!trigger || !isAppStudioCardHeader(trigger)) {
                return;
            }

            activateCollapseTrigger(event, trigger, eventTarget, windowRef, documentRef);
        },
        true
    );

    documentRef.addEventListener('shown.bs.collapse', (event) => {
        if (event.target instanceof HTMLElement) {
            syncLegacyCollapseTriggers(event.target, true, documentRef);
        }
    });

    documentRef.addEventListener('hidden.bs.collapse', (event) => {
        if (event.target instanceof HTMLElement) {
            syncLegacyCollapseTriggers(event.target, false, documentRef);
        }
    });
}
